import type { ComponentProps, FunctionComponent, ReactNode } from 'react';
import { useRef } from 'react';

import { useShakeOnClickOutside } from '../../animations/shake/use-shake-on-click-outside';
import { Button } from '../../core/button/Button';
import { Popover, PopoverAnchor, PopoverContent, PopoverTrigger } from '../../core/popover/Popover';
import { ShortcutTooltip, type ShortcutTooltipProps } from '../../core/shortcut-tooltip/ShortcutTooltip';
import { Textarea, type TextareaProps } from '../../core/textarea/Textarea';

type PromptComposerActionButtonSize = 'default' | NonNullable<ComponentProps<typeof Button>['size']>;

type PromptComposerBaseProps = {
    readonly actionButtonSize?: PromptComposerActionButtonSize;
    readonly cancelLabel: ReactNode;
    readonly children: ReactNode;
    /** Rendered at the start (left) of the footer row, opposite the cancel/submit actions. */
    readonly footerStart?: ReactNode;
    readonly isSubmitting?: boolean;
    readonly onCancel: () => void;
    readonly onSubmit: () => void;
    readonly submitDisabled?: boolean;
    readonly submitLabel: ReactNode;
    readonly submitTestId?: string;
};

type PromptComposerMode = 'inline' | 'popover' | 'rail';

const FOCUSABLE_SELECTOR =
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

const getComposerSpacing = (
    mode: PromptComposerMode,
): { contentGapClass: string; paddingClass: string; radiusClass: string } =>
    mode === 'inline'
        ? { contentGapClass: 'gap-l', paddingClass: 'p-0', radiusClass: '' }
        : {
              contentGapClass: mode === 'rail' ? 'gap-l' : 'gap-xl',
              paddingClass: 'p-xl',
              radiusClass: 'rounded-xxxl',
          };

type PromptComposerPopoverProps = PromptComposerBaseProps &
    Pick<ComponentProps<typeof PopoverContent>, 'align'> & {
        /**
         * Anchor the popover to `trigger` without giving it button semantics. Set this when the
         * caller can only supply a non-interactive wrapper: `aria-expanded` is invalid on a plain
         * span or div. Radix only restores focus to a real trigger, so in this mode the composer
         * restores focus itself, to the first element in `trigger` that accepts it. With no such
         * element focus falls to the body, exactly as it did before.
         */
        readonly anchorOnly?: boolean;
        readonly isOpen: boolean;
        readonly mode: 'popover';
        readonly shortcutTooltip?: Omit<ShortcutTooltipProps, 'children'>;
        readonly trigger: ReactNode;
        readonly width?: string;
    };

type PromptComposerRailProps = PromptComposerBaseProps & {
    readonly mode: Extract<PromptComposerMode, 'rail'>;
};

type PromptComposerInlineProps = PromptComposerBaseProps & {
    readonly mode: Extract<PromptComposerMode, 'inline'>;
};

type PromptComposerProps = PromptComposerPopoverProps | PromptComposerRailProps | PromptComposerInlineProps;

type PromptComposerInputProps = Omit<TextareaProps, 'className' | 'variant'> & {
    readonly actions?: ReactNode;
    readonly recordingOverlay?: ReactNode;
    readonly textareaClassName?: string;
};

export const PromptComposerInput: FunctionComponent<PromptComposerInputProps> = ({
    actions,
    recordingOverlay,
    textareaClassName = '',
    ...textareaProps
}) => {
    const isRecordingOverlayVisible = recordingOverlay !== undefined;

    return (
        <div className="relative w-full">
            <Textarea
                {...textareaProps}
                className={`block transition-opacity duration-200 ease ${actions === undefined ? '' : 'pb-8xl pr-8xl'} ${isRecordingOverlayVisible ? 'pointer-events-none opacity-0' : ''} ${textareaClassName}`}
            />
            {isRecordingOverlayVisible && (
                <div className="absolute inset-0 overflow-hidden rounded-md border border-border-subtle bg-surface-raised">
                    {recordingOverlay}
                </div>
            )}
            {actions !== undefined && !isRecordingOverlayVisible && (
                <div className="absolute bottom-s right-s flex items-center gap-s">{actions}</div>
            )}
        </div>
    );
};

type PromptComposerSurfaceProps = PromptComposerBaseProps & {
    readonly contentGapClass: string;
};

const PromptComposerSurface: FunctionComponent<PromptComposerSurfaceProps> = ({
    actionButtonSize = 'sm',
    cancelLabel,
    children,
    contentGapClass,
    footerStart,
    isSubmitting = false,
    onCancel,
    onSubmit,
    submitDisabled = false,
    submitLabel,
    submitTestId,
}) => {
    const buttonSize: ComponentProps<typeof Button>['size'] =
        actionButtonSize === 'default' ? undefined : actionButtonSize;

    const actionButtons = (
        <div className="flex items-center justify-end gap-s">
            <Button size={buttonSize} variant="tertiary" onClick={onCancel}>
                {cancelLabel}
            </Button>
            <Button
                data-testid={submitTestId}
                disabled={submitDisabled || isSubmitting}
                size={buttonSize}
                onClick={onSubmit}
            >
                {submitLabel}
            </Button>
        </div>
    );

    return (
        <div className={`flex w-full flex-col items-end ${contentGapClass}`}>
            {children}
            {footerStart === undefined ? (
                actionButtons
            ) : (
                <div className="flex w-full items-center justify-between">
                    {footerStart}
                    {actionButtons}
                </div>
            )}
        </div>
    );
};

export const PromptComposer: FunctionComponent<PromptComposerProps> = (props) => {
    const { isShaking, shake } = useShakeOnClickOutside();
    const anchorRef = useRef<HTMLDivElement>(null);
    const { contentGapClass, paddingClass, radiusClass } = getComposerSpacing(props.mode);
    const shadowClass = props.mode === 'popover' ? 'shadow-lg' : 'shadow-md';

    const handlePointerDownOutside = (event: Event): void => {
        shake();
        event.preventDefault();
    };

    if (props.mode === 'rail') {
        return (
            <div
                className={`motion-safe:animate-fade-in flex flex-col items-end ${radiusClass} bg-surface-hover ${paddingClass} font-sans ${shadowClass}`}
            >
                <PromptComposerSurface {...props} contentGapClass={contentGapClass} />
            </div>
        );
    }

    if (props.mode === 'inline') {
        return <PromptComposerSurface {...props} contentGapClass={contentGapClass} />;
    }

    const { align = 'end', anchorOnly = false, isOpen, shortcutTooltip, trigger, ...surfaceProps } = props;
    const width = props.width ?? '350px';
    // Shake the popover surface itself on outside-click. When not shaking, suppress the Radix
    // entrance animation per side so it doesn't replay once the shake clears.
    const animationClass = isShaking
        ? '!animate-popover-shake'
        : 'data-[side=bottom]:!animate-none data-[side=left]:!animate-none data-[side=right]:!animate-none data-[side=top]:!animate-none';

    // The tooltip wraps the trigger rather than the other way round: Radix's `asChild` clones its
    // child to attach the popover's anchor ref, and ShortcutTooltip neither forwards a ref nor
    // passes unknown props through — nesting it inside would leave the popover without an anchor.
    // `Trigger` stays the default so real trigger buttons keep `aria-expanded` and get focus back
    // on close; an `anchorOnly` wrapper can carry the first but not the second, so the composer
    // restores focus itself below.
    const popoverTrigger = anchorOnly ? (
        <PopoverAnchor asChild={true} ref={anchorRef}>
            {trigger}
        </PopoverAnchor>
    ) : (
        <PopoverTrigger asChild={true}>{trigger}</PopoverTrigger>
    );

    // Radix focuses `triggerRef` on close, and an anchor never populates it — focus would land on
    // the body. Restoring it inside the anchor mirrors what `PopoverTrigger` does, and unlike the
    // element that happened to be focused at open time it survives that element being unmounted
    // (the anchored composer is typically opened from a menu item that closes with it).
    const restoreFocusToAnchor = (event: Event): void => {
        const anchor = anchorRef.current;

        if (anchor === null) return;

        const candidates = [
            ...(anchor.matches(FOCUSABLE_SELECTOR) ? [anchor] : []),
            ...anchor.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
        ];

        // A candidate that is hidden, `inert` or `aria-disabled` silently refuses focus, so take the
        // first one that actually accepts it rather than trusting the selector.
        for (const candidate of candidates) {
            candidate.focus();

            if (document.activeElement === candidate) {
                event.preventDefault();

                return;
            }
        }
    };

    return (
        <Popover open={isOpen}>
            {shortcutTooltip === undefined ? (
                popoverTrigger
            ) : (
                <ShortcutTooltip {...shortcutTooltip}>{popoverTrigger}</ShortcutTooltip>
            )}
            <PopoverContent
                align={align}
                className={`flex flex-col items-end ${radiusClass} border-none bg-surface-hover ${paddingClass} ${shadowClass} ${animationClass}`}
                style={{ width }}
                onCloseAutoFocus={anchorOnly ? restoreFocusToAnchor : undefined}
                // Dismissal is routed here rather than through `onOpenChange`: that would also let
                // focus leaving the surface close it, discarding a half-written prompt, which is the
                // very thing `handlePointerDownOutside` shakes instead of doing.
                onEscapeKeyDown={(event) => {
                    event.preventDefault();
                    surfaceProps.onCancel();
                }}
                onPointerDownOutside={handlePointerDownOutside}
            >
                <PromptComposerSurface {...surfaceProps} contentGapClass={contentGapClass} />
            </PopoverContent>
        </Popover>
    );
};
